import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play, Clapperboard } from "lucide-react";
import { fetchHomeVideos } from "@/lib/api";

/**
 * Right-column video showcase on Dealer Home — mirrors FeaturedSidebar's
 * layout (brand strip + hero + single-item carousel with arrows + dots) but
 * in a cyan→blue theme. Videos are admin-curated (checked in the Gallery) and
 * served from `/postcue/home-videos`. Each item shows a poster frame with a
 * big play button; clicking plays the video inline.
 */
export default function VideoSidebar({ dealerSlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetchHomeVideos(dealerSlug);
        if (!cancel) setItems(res?.videos || []);
      } catch {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [dealerSlug]);

  // Reset playback whenever the active slide changes.
  useEffect(() => {
    setPlaying(false);
  }, [idx]);

  if (loading || items.length === 0) return null;

  const safeIdx = ((idx % items.length) + items.length) % items.length;
  const current = items[safeIdx];

  const handlePlay = () => {
    setPlaying(true);
    // Let the <video> mount its controls, then start playback.
    requestAnimationFrame(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(() => {});
      }
    });
  };

  return (
    <div className="space-y-3" data-testid="video-sidebar-wrap">
      {/* Cyan brand strip — same template as the violet products strip. */}
      <div className="rounded-t-2xl bg-cyan-200/70 px-5 py-3 flex items-center">
        <div className="font-semibold text-cyan-900 tracking-tight whitespace-nowrap">
          Watch &amp; Learn — Video Library
        </div>
      </div>

      {/* Cyan→blue hero. */}
      <div className="rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-700 text-white px-5 py-7 min-h-[140px] flex flex-col justify-center">
        <h2 className="text-xl font-semibold tracking-tight leading-tight flex items-center gap-2">
          <Clapperboard size={22} className="text-cyan-100 shrink-0" />
          See the <span className="text-cyan-200">Science</span> in Motion
        </h2>
        <p className="mt-2 text-xs text-white/90 leading-relaxed">
          Product demos, training &amp; results — straight from the lab.
        </p>
      </div>

      {/* Video carousel */}
      <div
        className="rounded-2xl overflow-hidden border border-cyan-100 bg-white"
        data-testid="video-sidebar"
      >
        <div className="p-4 relative">
          <div className="rounded-xl border border-cyan-100 overflow-hidden bg-black">
            <div className="relative aspect-video bg-black">
              {playing ? (
                <video
                  ref={videoRef}
                  src={current.url}
                  className="w-full h-full object-contain bg-black"
                  controls
                  autoPlay
                  playsInline
                  data-testid={`video-player-${safeIdx}`}
                />
              ) : (
                <button
                  type="button"
                  onClick={handlePlay}
                  className="group absolute inset-0 w-full h-full flex items-center justify-center"
                  data-testid={`video-poster-${safeIdx}`}
                  aria-label={`Play ${current.title || "video"}`}
                >
                  {/* First-frame poster */}
                  <video
                    src={`${current.url}#t=0.1`}
                    className="absolute inset-0 w-full h-full object-cover opacity-90"
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent" />
                  <span className="relative w-16 h-16 rounded-full bg-cyan-500/90 group-hover:bg-cyan-400 text-white flex items-center justify-center shadow-lg transition-colors">
                    <Play size={26} className="ml-1" fill="currentColor" />
                  </span>
                </button>
              )}
            </div>
          </div>

          {current.title && (
            <h4 className="font-semibold text-zinc-900 text-base leading-snug tracking-tight mt-3 line-clamp-2">
              {current.title}
            </h4>
          )}

          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => setIdx(safeIdx - 1)}
                className="absolute top-[38%] -left-1 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-cyan-200 shadow-md flex items-center justify-center text-zinc-600 hover:text-blue-700 hover:border-blue-300"
                aria-label="Previous video"
                data-testid="video-sidebar-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => setIdx(safeIdx + 1)}
                className="absolute top-[38%] -right-1 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-cyan-200 shadow-md flex items-center justify-center text-zinc-600 hover:text-blue-700 hover:border-blue-300"
                aria-label="Next video"
                data-testid="video-sidebar-next"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}

          {items.length > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-3">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === safeIdx ? "w-5 bg-blue-600" : "w-1.5 bg-zinc-300"
                  }`}
                  aria-label={`Go to video ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
